import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "@/reducers/types";
import { KeepOutAreaPolygon, Rectangle, UserRole, UserRoles } from "@/sx-layout/components/plotmap/models";
import { useTerm } from "@/sx-layout/hooks";
import { useLoginUser } from "@/sx-layout/hooks/useLoginUser";

export const useSelectedArea = () => {
  const [freeDrawArea, setFreeDrawArea] = useState<KeepOutAreaPolygon>();
  const [rect, setRect] = useState<Rectangle>();
  const [dragging, setDragging] = useState(false);

  // 権限制御
  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
  const { isPast } = useTerm(layoutDate);
  const userRole = useSelector<RootState, UserRoles>((state) => state.layoutApp.layoutMasters.user_role);
  const { loginUser } = useLoginUser();

  const hasRole = useMemo(() => {
    const isAdmin = userRole === UserRole.MASTER_ROLE;
    const isUserAndCompany =
      userRole === UserRole.USER_ROLE && freeDrawArea && freeDrawArea.company_id === loginUser?.company_id;

    return isAdmin || isUserAndCompany;
  }, [freeDrawArea, loginUser?.company_id, userRole]);

  const isEditable = useMemo(() => {
    return !isPast && hasRole;
  }, [isPast, hasRole]);

  const start = useCallback((area: KeepOutAreaPolygon, rect: Rectangle) => {
    setFreeDrawArea(area);
    setRect(rect);
  }, []);

  const end = useCallback(() => {
    setFreeDrawArea(null);
    setRect(null);
  }, []);

  const changeCoords = useCallback(
    (area: KeepOutAreaPolygon) => {
      setFreeDrawArea({
        ...freeDrawArea,
        coords: area.coords,
        timestamp: { ...area.timestamp, update_date: area.timestamp.update_date },
      });
    },
    [freeDrawArea]
  );

  const startDrag = () => {
    setDragging(true);
  };

  const endDrag = (rect: Rectangle) => {
    setRect(rect);
    setDragging(false);
  };

  const centerX = useCallback((): number => {
    if (!rect) return 0;

    return rect.x + rect.width / 2;
  }, [rect]);

  return { freeDrawArea, start, end, isEditable, hasRole, changeCoords, rect, centerX, startDrag, endDrag, dragging };
};
